import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { ProductService, SpecificationsDto } from '@/api-sdk';
import {
  BREAKPOINT_LARGE_PX,
  BREAKPOINT_MEDIUM_PX,
  BREAKPOINT_SMALL_PX,
} from '@/settings';
import { baseStyles, rootVariablesStyles, typographyStyles } from '@/styles';
import { setBaseUrl } from '@/utils';

import '../loader/fg-loader';

@customElement('fg-product-specifications-table')
export class FgProductSpecificationsTable extends LitElement {
  /**
   * Product code/sku, and is a required property used to fetch the product specifications.
   *
   * @property product
   */
  @property({ type: String })
  product = '';

  /**
   * Base url which is used to set for correct API calls.
   *
   * @property baseUrl
   */
  @property({ type: String })
  baseUrl = '';

  /**
   * productSpecifications returns a array with specifications grouped by specified title of the specifications.
   *
   * @property productSpecifications
   */
  @property({ type: Array })
  productSpecifications: SpecificationsDto[] = [];

  /**
   * Locale string which is used to fetch the product specifications.
   *
   * @property {string} locale
   */
  @property({ type: String })
  locale = 'nl_NL';

  /**
   * Boolean to show the loading state of the component
   */
  @state()
  isLoading = true;

  async firstUpdated() {
    setBaseUrl(this.baseUrl);
    this.getProductSpecifications();
  }

  async getProductSpecifications() {
    try {
      const productSpecifications = await ProductService.getShowProductSpecs({
        locale: this.locale,
        code: this.product,
      });

      if (productSpecifications) {
        this.isLoading = false;
        this.productSpecifications = productSpecifications;
      }
    } catch {
      this.isLoading = false;
    }
  }

  render() {
    if (this.isLoading) {
      return html`
        <fg-loader type="configuration"></fg-loader>
      `;
    }

    return html`
      ${this.productSpecifications.map(specificationItem => {
        const { specifications, title } = specificationItem;
        if (specifications.length > 0) {
          return html`
            <section>
              <h3>${title}</h3>
              <table>
                <tbody>
                  ${specifications.map(specification => {
                    return html`
                      <tr>
                        <td>${specification.label}</td>
                        <td data-title="${specification.label}">
                          ${specification.value}
                        </td>
                      </tr>
                    `;
                  })}
                </tbody>
              </table>
            </section>
          `;
        }
      })}
    `;
  }

  static styles = [
    baseStyles,
    rootVariablesStyles,
    typographyStyles,
    css`
      section {
        margin-top: 24px;
      }
      .header-price-table {
        margin-bottom: 16px;
      }

      table {
        border-collapse: collapse;
        margin-top: 8px;
        width: 100%;
      }
      tr:nth-of-type(odd) {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
      }
      td {
        padding: 8px 11px;
        vertical-align: baseline;
      }
      td:nth-of-type(odd) {
        display: none;
      }
      td:nth-of-type(even) {
        border-radius: 4px;
        font-weight: 700;
        width: 100%;
      }
      td:nth-of-type(even)::before {
        content: attr(data-title);
        display: block;
        font-weight: 400;
        width: 100%;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        section {
          margin-top: 48px;
        }

        td:nth-of-type(even)::before {
          content: '';
          display: none;
        }
      }
      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        td {
          padding: 11px 24px;
        }
        td:nth-of-type(odd) {
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
          display: table-cell;
          width: 50%;
        }
        td:nth-of-type(even) {
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;
          width: 50%;
        }
      }
      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        td:nth-of-type(odd) {
          width: 66%;
        }
        td:nth-of-type(even) {
          width: 33%;
        }
      }
    `,
  ];
}
